// src/components/AddProject.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Axios from "../../utils/Axios";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/AddProject.css";
import validateForm from "../../utils/Validation";
import AuthHandler from "../../utils/AuthHandler";
import { useDispatch, useSelector } from "react-redux";
import {
  FETCH_CLIENTS_REQUEST,
  FETCH_EMPLOYEES_REQUEST,
  UPLOAD_PROJECT_FILE_REQUEST,
  CLEAR_UPLOADED_FILES,
  REMOVE_UPLOADED_FILE,
} from "../../store/Actions/Actions";
import { CREATE_NEW_PROJECT, PROJECT_DETAILS_PID } from "../../config/API_Collection";
import SearchableDropdown from "../../components/DropdownSearch";

function AddProject({
  closeModal,
  showToast,
  update: { checkUpdate, setCheckUpdate },
}) {
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [progress, setProgress] = useState({});
  // Per-row user-entered custom file names (keeps input separate from uploaded file name)
  const [customNames, setCustomNames] = useState([]);
  // UI: drag state for dropzone highlight
 
  const uploadState = useSelector((_) => _?.upload);
  const clients = useSelector((_) => _?.clientsList?.clients || []);
  const employees = useSelector((_) => _?.employees?.employees || []);
  const [toast, setToast] = useState({ message: "", type: "" });
  const navigate = useNavigate();
  const [formErrors, setFormErrors] = useState({});
  const DISPATCH = useDispatch();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // Which document slot the hidden file input is targeting (0 or 1)
  const [uploadTargetIndex, setUploadTargetIndex] = useState(0);
  const prevUploadedCountRef = useRef(0);

  // ---- Local state for SearchableDropdown selections (labels) + hidden values ----
  const [clientLabel, setClientLabel] = useState("");
  const [clientId, setClientId] = useState("");

  const [pmLabel, setPmLabel] = useState("");
  const [pmId, setPmId] = useState("");

  const [feeLabel, setFeeLabel] = useState("");
  const [feeValue, setFeeValue] = useState("");

  const [officeLabel, setOfficeLabel] = useState("");
  const [officeValue, setOfficeValue] = useState("");

  const [segmentLabel, setSegmentLabel] = useState("");
  const [segmentValue, setSegmentValue] = useState("");

  // ---- Static option maps ----
  const feeMap = useMemo(
    () => ({
      Fixed: 1,
      Hourly: 2,
    }),
    []
  );
  const feeOptions = useMemo(() => Object.keys(feeMap), [feeMap]);

  // UPDATED: Office list now includes Napier, Rotorua, Thames, Wellington
  const officeOptions = useMemo(
    () => ["Hamilton", "Auckland", "Napier", "Rotorua", "Thames", "Wellington"],
    []
  );

  const segmentOptions = useMemo(
    () => [
      "Architecture",
      "Assessment",
      "Cultural",
      "Electrical",
      "Environmental",
      "Flooding",
      "Geotech",
      "Heritage",
      "Infrastructure",
      "Insurance",
      "Peer Review",
      "Planning consents",
      "Seismic",
      "Structural",
      "Surveying",
      "Three waters",
      "Training",
      "Transport",
      "Wind Assessment",
    ],
    []
  );

  // ---- Derived options from store ----
  const clientOptions = useMemo(
    () =>
      clients
        .filter((c) => c?.status === 1)
        .map((c) => c?.client_name)
        .filter(Boolean),
    [clients]
  );

  const employeeOptions = useMemo(
    () =>
      employees
        .map((e) => `${e?.first_name ?? ""} ${e?.last_name ?? ""}`.trim())
        .filter(Boolean),
    [employees]
  );

 
  const handleFileSelect = (e) => {
    // Accept only a single file from the input (one-at-a-time behaviour)
    const file = e.target.files && e.target.files[0];
    if (file) handleFiles([file], uploadTargetIndex);
    // Clear the input so the same file can be selected again later
    e.target.value = "";
  };
 
  const handleFiles = (files, slotIndex = undefined) => {
    // Initiate upload for each file via saga with optional per-slot custom name
    files.forEach((file, idx) => {
      // Use the custom document name from the input field if available
      const baseName = (typeof slotIndex === 'number' && customNames[slotIndex])
        ? customNames[slotIndex].trim()
        : "";
      
      // If no custom name provided, fall back to the original file name
      const file_name = baseName || file.name;
      
      // Initialize progress at 0 and start smooth animation
      setProgress((prev) => ({ ...prev, [file_name]: 0 }));
      
      // Simulate smooth progress from 0 to 90% over ~2.5 seconds
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          const current = prev[file_name] || 0;
          if (current >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          // Increment by 5-10% each 200ms for smooth animation
          const increment = Math.random() * 5 + 5;
          return { ...prev, [file_name]: Math.min(90, current + increment) };
        });
      }, 200);
      
      DISPATCH({
        type: UPLOAD_PROJECT_FILE_REQUEST,
        payload: { file, file_name, section: "project", slotIndex },
      });

      // Insert file descriptor into the uploadingFiles array at the requested slotIndex (if provided)
      if (typeof slotIndex === "number") {
        setUploadingFiles((prev) => {
          const arr = [...prev];
          // ensure array length
          while (arr.length <= slotIndex) arr.push(null);
          arr[slotIndex] = { name: file_name, originalFile: file };
          return arr;
        });
      } else {
        setUploadingFiles((prev) => [...prev, { name: file_name, originalFile: file }]);
      }
    });
  };
 
  const handleFileRemove = (fileName, slotIndex = undefined) => {
    // Prefer removing by slotIndex when provided (more reliable)
    let localIndex = -1;
    if (typeof slotIndex === 'number') {
      localIndex = slotIndex;
    } else {
      localIndex = uploadingFiles.findIndex((f) => f && f.name === fileName);
    }

    // Debug
    // console.debug('handleFileRemove called', { fileName, slotIndex, uploadingFiles, uploadState });

    // Remove the local uploadingFiles entry and compact the array
    setUploadingFiles((prev) => {
      const arr = [...prev];
      if (localIndex !== -1 && localIndex < arr.length) {
        arr.splice(localIndex, 1);
      } else if (fileName) {
        return arr.filter((f) => f && f.name !== fileName);
      }
      return arr.filter(Boolean);
    });

    // Remove corresponding custom name at the same index so rows remain aligned
    setCustomNames((prev) => {
      const arr = [...prev];
      if (localIndex !== -1 && localIndex < arr.length) {
        arr.splice(localIndex, 1);
      }
      return arr.filter((v) => v !== undefined && v !== null);
    });

    // Remove progress entry (remove any keys that match the file name or uploadedMatch name)
    setProgress((prev) => {
      const updated = { ...prev };
      // remove exact key
      if (fileName && updated[fileName]) delete updated[fileName];
      // also remove any keys equal to or containing the fileName
      Object.keys(updated).forEach((k) => {
        if (fileName && k === fileName) delete updated[k];
        if (fileName && typeof k === 'string' && k.includes && fileName && k.includes(fileName)) delete updated[k];
      });
      return updated;
    });

    // If this file exists in the uploaded array (redux), remove it there too so UI doesn't persist it
    // Try to find by server-provided _slotIndex first, then by file_name
    let uploadedMatch = null;
    if (typeof slotIndex === 'number') {
      uploadedMatch = (uploadState?.uploaded || []).find((u) => typeof u?._slotIndex === 'number' && u._slotIndex === slotIndex);
    }
    if (!uploadedMatch && fileName) {
      uploadedMatch = uploadState?.uploaded?.find((u) => u?.file_name === fileName) || null;
    }

    if (uploadedMatch) {
      const sendName = uploadedMatch.file_name || uploadedMatch.fileName || uploadedMatch.filename || uploadedMatch.name;
      // dispatch slotIndex as well to make reducer removal exact
      DISPATCH({ type: REMOVE_UPLOADED_FILE, payload: { file_name: sendName, document_id: uploadedMatch.document_id, slotIndex: localIndex } });
    }
  };
  const getProgressBarColor = (value) => {
    // Use blue for in-progress and green when complete to match mock
    if (value <= 0) return "progress-bar bg-light";
    if (value < 100) return "progress-bar bg-primary";
    return "progress-bar bg-primary";
  };

  const getDisplayProgress = (name) => {
    const p = progress?.[name];
    if (typeof p === "number") return p;
    // If uploaded in store, show 100
    const uploadedMatch = uploadState?.uploaded?.find((d) => d?.file_name === name);
    if (uploadedMatch) return 100;
    // If the file is currently queued in uploadingFiles, show 0 (will be animated)
    if (uploadingFiles.find((f) => f && f.name === name)) return 0;
    return 0;
  };

  useEffect(() => {
    DISPATCH({ type: FETCH_CLIENTS_REQUEST, payload: null });
    DISPATCH({ type: FETCH_EMPLOYEES_REQUEST, payload: null });
    setStartDate(new Date());
  }, []);

  // Toast feedback for upload success/error
  useEffect(() => {
    const current = uploadState?.uploaded?.length || 0;
    const prev = prevUploadedCountRef.current;
    if (current > prev) {
      showToast("File uploaded successfully", "success");
    }
    prevUploadedCountRef.current = current;
  }, [uploadState?.uploaded]);

  // When uploadState reports uploaded files, mark their progress as complete (100%).
  useEffect(() => {
    const uploaded = uploadState?.uploaded || [];
    if (!uploaded || uploaded.length === 0) return;

    setProgress((prev) => {
      const updated = { ...prev };
      uploaded.forEach((u) => {
        // Prefer explicit slot mapping from the server if provided
        if (typeof u?._slotIndex === 'number') {
          const idx = u._slotIndex;
          const slotEntry = uploadingFiles[idx];
          if (slotEntry && slotEntry.name) {
            updated[slotEntry.name] = 100;
            return;
          }
        }

        // Fallback: try to match by the name we used when initiating upload (uploadingFiles entry)
        const slotIndex = uploadingFiles.findIndex(
          (f) => f && (f.name === u.file_name || f.originalFile?.name === u.file_name)
        );
        if (slotIndex !== -1) {
          const slotName = uploadingFiles[slotIndex].name;
          updated[slotName] = 100;
        } else if (u.file_name) {
          // fallback: ensure uploaded file shows 100% by its returned file_name
          updated[u.file_name] = 100;
        }
      });
      return updated;
    });

    // Remove uploaded files from uploadingFiles array to re-enable the button
    setUploadingFiles((prev) => {
      const remaining = prev.filter((uploadingFile) => {
        if (!uploadingFile) return false;
        // Check if this file has been uploaded - compare against document_name from API
        const isUploaded = uploaded.some((u) => {
          // Extract data from nested response structure
          const uploadData = u?.data?.data || u?.data || u;
          const documentName = uploadData?.document_name || uploadData?.name;
          const uploadingName = uploadingFile.name;
          
          console.log('Comparing:', { documentName, uploadingName, match: documentName === uploadingName });
          
          return documentName === uploadingName;
        });
        return !isUploaded; // Keep only files that haven't been uploaded yet
      });
      
      // Only update if there's a change
      if (remaining.length !== prev.length) {
        console.log('Removing uploaded files from queue. Before:', prev.length, 'After:', remaining.length);
        return remaining;
      }
      return prev;
    });
  }, [uploadState?.uploaded, uploadingFiles]);

  // Reset local upload-related state (used when closing modal or after successful create)
  const resetUploadState = () => {
    setUploadingFiles([]);
    setCustomNames([]);
    setProgress({});
    // Clear uploaded files from redux store as well so modal re-open starts fresh
    DISPATCH({ type: CLEAR_UPLOADED_FILES, payload: null });
  };

  useEffect(() => {
    const err = uploadState?.error;
    if (!err) return;
    // uploadState.error may be a string or object (we dispatch object from saga)
    const message = typeof err === "string" ? err : err.message || err.msg || err || "Upload failed";
    showToast(message, "danger");

    // If the error payload includes a slotIndex or file_name, remove the local slot so UI doesn't hang
    try {
      const slotIndex = err?.slotIndex;
      const fileName = err?.file_name || (err?.details && err.details.file_name) || null;
      if (typeof slotIndex === "number") {
        // call remove by slotIndex
        handleFileRemove(fileName || null, slotIndex);
      } else if (fileName) {
        handleFileRemove(fileName);
      }
    } catch (e) {
      // ignore
    }
  }, [uploadState?.error]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formData = e.target;

      // Build attachments strictly as an array of document_id numbers
      const attachments = Array.from(
        new Set(
          (uploadState?.uploaded || [])
            .map((d) => {
              // Handle various possible response structures
              const docId = d?.document_id || d?.data?.document_id || d?.id || d?.data?.id;
              return docId;
            })
            .filter((id) => id !== null && id !== undefined && !Number.isNaN(Number(id)))
            .map((id) => Number(id))
        )
      );

      const payload = {
        project_name: formData.project_name.value,
        client_id: parseInt(formData.client_id.value),
        project_manager: parseInt(formData.project_manager.value),
        project_type: parseInt(formData.project_type.value),
        office_location: formData.office_location.value,
        market_segment: formData.market_segment.value,
        budget_pricing: parseInt(formData.budget_pricing.value),
        attachments,
        start_date: formData.start_date.value,
        end_date: formData.end_date.value,
      };

      const requiredFields = [
        "project_name",
        "client_id",
        "project_manager",
        "market_segment",
        "project_type",
        "budget_pricing",
        "start_date",
      ];
      const errors = validateForm(payload, requiredFields);
      setFormErrors(errors);
      if (Object.keys(errors).length > 0) return;

      const response = await Axios.post(CREATE_NEW_PROJECT, payload);
      if (response.status === 200) {
        showToast("Project Created Successfully!", "success");

        // Try to extract created project id from common response shapes
        const respData = response?.data || {};
        const maybeId =
          respData?.data?.pid ||
          respData?.data?.project_id ||
          respData?.pid ||
          respData?.project_id ||
          respData?.id ||
          null;

        // Verify attachments on the server by fetching project details if we have an id
        if (maybeId) {
          try {
            const detailsResp = await Axios.get(`${PROJECT_DETAILS_PID}${maybeId}`);
            const details = detailsResp?.data?.data || detailsResp?.data || {};

            // Find any arrays in details that look like attachments/documents
            const extractIdsFromObj = (obj) => {
              if (!obj || typeof obj !== "object") return [];
              const ids = [];
              Object.keys(obj).forEach((k) => {
                const val = obj[k];
                if (Array.isArray(val)) {
                  // array of objects with document_id / id
                  val.forEach((it) => {
                    if (it && typeof it === "object") {
                      if (it.document_id || it.id || it.attach_id || it.file_id) {
                        ids.push(Number(it.document_id || it.id || it.attach_id || it.file_id));
                      }
                    }
                  });
                }
              });
              return ids;
            };

            const returnedIds = extractIdsFromObj(details);
            const sentIds = attachments || [];

            // Compare sets
            const setA = new Set(sentIds.map((n) => Number(n)));
            const setB = new Set(returnedIds.map((n) => Number(n)));
            const equalSets = setA.size === setB.size && [...setA].every((v) => setB.has(v));

            if (equalSets) {
              showToast("Attachments verified on server.", "success");
            } else {
              // Not critical, warn in console and show info toast
              console.warn("Attachment verification mismatch", { sentIds, returnedIds, details });
              showToast("Project created but attachments verification mismatch.", "warning");
            }
          } catch (err) {
            console.warn("Failed to verify project attachments", err);
            showToast("Project created (verification failed).", "info");
          }
        }

        // clear local upload UI before closing
        resetUploadState();
        closeModal();
        setCheckUpdate(!checkUpdate);
      }
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
    }
  };

  // ---- Selection handlers to keep hidden inputs in sync ----
  const onSelectClient = (label) => {
    setClientLabel(label);
    const match = clients.find((c) => c?.client_name === label && c?.status === 1);
    setClientId(match ? String(match.client_id) : "");
  };

  const onSelectPM = (label) => {
    setPmLabel(label);
    const match = employees.find(
      (e) => `${e?.first_name ?? ""} ${e?.last_name ?? ""}`.trim() === label
    );
    setPmId(match ? String(match.employee_id) : "");
  };

  const onSelectFee = (label) => {
    setFeeLabel(label);
    setFeeValue(feeMap[label] ? String(feeMap[label]) : "");
  };

  const onSelectOffice = (label) => {
    setOfficeLabel(label);
    setOfficeValue(label || "");
  };

  const onSelectSegment = (label) => {
    setSegmentLabel(label);
    setSegmentValue(label || "");
  };

  // NOTE: rendering now uses uploadingFiles entries which are objects { name, originalFile }

  return (
    <div className="add-project-container">
      <form className="project-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div
            className={`form-group flex-item ${
              formErrors.project_name ? "has-error" : ""
            }`}
          >
            <label>Project Name *</label>
            <input type="text" name="project_name" />
          </div>

          <div
            className={`form-group flex-item ${
              formErrors.client_id ? "has-error" : ""
            }`}
          >
            <label>Company *</label>
            <SearchableDropdown
              label="Company"
              allowAll={false}
              options={clientOptions}
              value={clientLabel}
              onChange={onSelectClient}
            />
            {/* Hidden input expected by backend */}
            <input type="hidden" name="client_id" value={clientId} />
          </div>
        </div>

        <div className="form-row">
          <div
            className={`form-group flex-item ${
              formErrors.project_manager ? "has-error" : ""
            }`}
          >
            <label>Project Manager *</label>
            <SearchableDropdown
              label="Project Manager"
              allowAll={false}
              options={employeeOptions}
              value={pmLabel}
              onChange={onSelectPM}
            />
            <input type="hidden" name="project_manager" value={pmId} />
          </div>

          <div
            className={`form-group flex-item ${
              formErrors.budget_pricing ? "has-error" : ""
            }`}
          >
            <label>Fee Proposal *</label>
            <SearchableDropdown
              label="Fee Proposal"
              allowAll={false}
              options={feeOptions}
              value={feeLabel}
              onChange={onSelectFee}
            />
            <input type="hidden" name="budget_pricing" value={feeValue} />
          </div>
        </div>

        <div className="form-row">
          <div
            className={`form-group flex-item ${
              formErrors.office_location ? "has-error" : ""
            }`}
          >
            <label>Office *</label>
            <SearchableDropdown
              label="Office"
              allowAll={false}
              options={officeOptions}
              value={officeLabel}
              onChange={onSelectOffice}
            />
            <input type="hidden" name="office_location" value={officeValue} />
          </div>

          <div className="form-group flex-item">
            <label>Market Segment *</label>
            <SearchableDropdown
              label="Market Segment"
              allowAll={false}
              options={segmentOptions}
              value={segmentLabel}
              onChange={onSelectSegment}
            />
            <input type="hidden" name="market_segment" value={segmentValue} />
          </div>
        </div>

        <div className="form-row">
          <div
            className={`form-group flex-item ${
              formErrors.project_type ? "has-error" : ""
            }`}
          >
            <label>Project Type *</label>
            <div className="radio-group">
              <label>
                <input type="radio" name="project_type" value="1" /> Billable
              </label>
              <label>
                <input type="radio" name="project_type" value="2" /> Non
                Billable
              </label>
            </div>
            {formErrors.project_type && (
              <div className="error-text">{formErrors.project_type}</div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group flex-item">
              <label>Start Date *</label>
              <DatePicker
                required
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="yyyy-MM-dd"
              />
              <input
                type="hidden"
                name="start_date"
                value={startDate ? startDate.toLocaleDateString("en-CA") : ""}
              />
            </div>

            <div className="form-group flex-item">
              <label>End Date</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="yyyy-MM-dd"
              />
              <input
                type="hidden"
                name="end_date"
                value={endDate ? endDate.toLocaleDateString("en-CA") : ""}
              />
            </div>
          </div>
        </div>

        {/* File Uploads - dynamic document rows */}
        <div className="form-row">
          <div className="form-group flex-item" style={{ padding: 0 }}>
            <label>Project Attachments</label>
            <div style={{ background: "#f5f5f6", padding: 20, borderRadius: 4 }}>
               <span style={{marginBottom: 10,fontSize: 12, fontWeight: 300, }}>Upload new attachments</span>
              {(() => {
                const uploadedLen = uploadState?.uploaded?.length || 0;
                const slotsCount = Math.max(uploadingFiles.length, uploadedLen) + 1; // always keep one empty slot
                return Array.from({ length: slotsCount }).map((_, i) => {
                  // Prefer uploaded item that includes a server-provided slot index
                  const uploadedWithIndex = (uploadState?.uploaded || []).find((u) => typeof u?._slotIndex === 'number' && u._slotIndex === i);
                  const uploadedFallback = uploadState?.uploaded && uploadState.uploaded[i]
                    ? { name: uploadState.uploaded[i].file_name, _uploaded: true }
                    : null;
                  const uploadedItem = uploadedWithIndex || uploadedFallback;
                  const slot = uploadingFiles[i] || uploadedItem || null;
                  return (
                    <div
                      className={`d-flex align-items-center ${i < slotsCount - 1 ? 'mb-3' : ''}`}
                      style={{ gap: 12 }}
                      key={`doc-row-${i}`}
                    >
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Documents Name"
                        value={customNames[i] || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCustomNames((prev) => {
                            const arr = [...prev];
                            while (arr.length <= i) arr.push("");
                            arr[i] = v;
                            return arr;
                          });
                        }}
                        style={{ flexBasis: "40%", maxWidth: 320 }}
                      />

                      <div style={{ flex: 1 }}>
                        {slot ? (
                          <div className="progress" style={{ height: 4, borderRadius: 6, overflow: "hidden" }}>
                            <div
                              className={getProgressBarColor(getDisplayProgress(slot.name))}
                              role="progressbar"
                              style={{ 
                                width: `${getDisplayProgress(slot.name)}%`, 
                                height: 10,
                                transition: 'width 0.3s ease-in-out'
                              }}
                              aria-valuenow={getDisplayProgress(slot.name)}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            ></div>
                          </div>
                        ) : (
                          <div />
                        )}
                      </div>

                      {slot ? (
                        <button
                          type="button"
                          className="btn btn-link text-secondary"
                          title="Remove"
                          onClick={() => handleFileRemove(slot.name, i)}
                          style={{ width: 36, height: 36 }}
                        >
                          <i className="bi bi-trash" style={{ fontSize: 18 }}></i>
                        </button>
                      ) : (
                        <div style={{ width: 36 }} />
                      )}

                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            // Validate that document name is filled before allowing upload
                            if (!customNames[i] || customNames[i].trim() === "") {
                              showToast("Please enter a document name before uploading", "warning");
                              return;
                            }
                            setUploadTargetIndex(i);
                            document.getElementById("projectUploadInput").click();
                          }}
                          style={{ marginLeft: 8 }}
                        >
                          <i className="bi bi-upload me-1"></i> File
                        </button>
                      </div>
                    </div>
                  );
                });
              })()}

              <input
                id="projectUploadInput"
                type="file"
                accept="image/*,application/pdf"
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={uploadingFiles.length > 0}>
            Create Project
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={(_) => {
              // clear local upload UI then close modal
              resetUploadState();
              closeModal();
              setCheckUpdate(!checkUpdate);
            }}
          >
            Cancel
          </button>
        </div>
        <div className="mt-3">
          {uploadState?.error && (
            <div className="alert alert-danger p-2">{uploadState.error}</div>
          )}
        </div>
      </form>
    </div>
  );
}

export default AddProject;
